import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Row, Col, Divider, Tooltip } from 'antd';
import { BankOutlined, LinkOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { buildChannelOptions, getCurrencyIcon } from '../utils/channelLogos';
import { testApi, metaApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';

const sectionTitle = (icon, text) => (
  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
    <span>{icon}</span><span style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{text}</span>
  </div>
);

function DynamicTipFormItem({ field, protocol, t }) {
  const label = t(field.label) || t(field.name) || field.label;
  const tip = t(field.tip, protocol);
  return (
    <Form.Item
      label={
        <Space size={4}>
          <span>{label}</span>
          {tip && (
            <Tooltip title={tip}>
              <span style={{ cursor: 'help', color: '#1890ff', fontSize: 12 }}>?</span>
            </Tooltip>
          )}
        </Space>
      }
      name={`extParam_${field.name}`}
      rules={[{ required: field.required, message: t('pleaseInput', label) }]}
      initialValue={field.default}
    >
      <Input placeholder={t('pleaseInput', label)} />
    </Form.Item>
  );
}

const channelExtParamFields = {
  EQUALS_MONEY: [
    { name: 'purpose', label: 'Purpose', type: 'input', required: true, default: 'AES' }
  ],
  LEAN_TECH: [
    { name: 'callbackUrl', label: 'Callback URL', type: 'input', required: true, default: 'https://your-domain.com/callback' },
    { name: 'bankAddress', label: 'Bank Address', type: 'input', required: true, default: '123 Bank Street' },
    { name: 'bankCity', label: 'Bank City', type: 'input', required: true, default: 'Hong Kong' }
  ],
  PYVIO: [
    { name: 'paymentMethod', label: 'Payment Method', type: 'select', required: true, options: ['LOCAL', 'SWIFT'], default: 'LOCAL' },
    { name: 'routeType', label: 'Route Type', type: 'input', required: false, default: 'RTGS' },
    { name: 'payeeType', label: 'Payee Type', type: 'select', required: true, options: ['BENEFICIARY', 'COMPANY'], default: 'BENEFICIARY' },
    { name: 'firstName', label: 'First Name', type: 'input', required: false },
    { name: 'lastName', label: 'Last Name', type: 'input', required: false },
    { name: 'payeeCompanyName', label: 'Payee Company Name', type: 'input', required: false },
    { name: 'payeeEmail', label: 'Payee Email', type: 'input', required: false },
    { name: 'payeePhone', label: 'Payee Phone', type: 'input', required: false },
    { name: 'payeeCountry', label: 'Payee Country', type: 'input', required: true, default: 'HK' },
    { name: 'payeeStreetAddress', label: 'Payee Street Address', type: 'input', required: true },
    { name: 'payeeCity', label: 'Payee City', type: 'input', required: true },
    { name: 'bankAddress', label: 'Bank Address', type: 'input', required: true },
    { name: 'bankCity', label: 'Bank City', type: 'input', required: true },
    { name: 'bankState', label: 'Bank State', type: 'input', required: false },
    { name: 'branchCode', label: 'Branch Code', type: 'input', required: false },
    { name: 'bankCode', label: 'Bank Code', type: 'input', required: false },
    { name: 'contactDocuments', label: 'Contact Documents', type: 'input', required: false },
    { name: 'bankStatement', label: 'Bank Statement', type: 'input', required: false },
  ],
  BANK_FRICK: [
    { name: 'paymentMethod', label: 'Payment Method', type: 'select', required: true, options: ['SWIFT', 'SEPA'], default: 'SWIFT' },
    { name: 'recipientAddress', label: 'Recipient Address', type: 'input', required: false, requiredWhen: { field: 'extParam_paymentMethod', value: 'SWIFT' } },
    { name: 'recipientCity', label: 'Recipient City', type: 'input', required: false, requiredWhen: { field: 'extParam_paymentMethod', value: 'SWIFT' } },
    { name: 'recipientPostalCode', label: 'Recipient Postal Code', type: 'input', required: false, requiredWhen: { field: 'extParam_paymentMethod', value: 'SWIFT' } },
    { name: 'recipientCountry', label: 'Recipient Country', type: 'input', required: false, default: 'CH', requiredWhen: { field: 'extParam_paymentMethod', value: 'SWIFT' } },
    { name: 'routeAba', label: 'Route ABA', type: 'input', required: false },
    { name: 'recipientId', label: 'Recipient ID', type: 'input', required: false }
  ],
  DA5: [
    { name: 'provider', label: 'Provider', type: 'select', required: true, options: ['instapay', 'pesonet'], default: 'instapay' },
  ],
  BANKING_CIRCLE: [
    { name: 'financialInstitution', label: 'Financial Institution (BIC)', type: 'input', required: false,
      requiredWhen: { field: 'bindType', value: 'BANKACCOUNT' } },
    { name: 'creditorAddressLine1', label: 'Creditor Address', type: 'input', required: true },
  ],
  BITSO: [
    { name: 'protocol', label: 'Protocol', type: 'select', required: true, options: ['clabe', 'debitcard', 'phonenum'], default: 'clabe' },
    { name: 'clabe', label: 'clabeAccountNo', type: 'input', required: true,
      tip: 'tipClabeByProtocol', tipDep: 'extParam_protocol' },
    { name: 'bankCode', label: 'Bank Code', type: 'input', required: false,
      requiredWhen: { field: 'extParam_protocol', values: ['debitcard', 'phonenum'] },
      tip: 'tipBankCodeDebitcard' },
  ],
  TAZAPAY: [
    { name: 'email', label: 'Email', type: 'input', required: false },
    { name: 'nationality', label: 'Nationality (ISO alpha-2)', type: 'input', required: false, default: '' },
    { name: 'taxId', label: 'Tax ID (CPF/CNPJ for BR)', type: 'input', required: false },
    { name: 'transferType', label: 'Transfer Type', type: 'select', required: false, options: ['swift', 'local'], default: 'swift' },
    { name: 'ifscCode', label: 'IFSC Code (India)', type: 'input', required: false },
    { name: 'abaCode', label: 'ABA Routing (US)', type: 'input', required: false },
    { name: 'sortCode', label: 'Sort Code (UK)', type: 'input', required: false },
    { name: 'bankCode', label: 'Bank Code (HK)', type: 'input', required: false },
    { name: 'accountType', label: 'Account Type', type: 'select', required: false, options: ['checking', 'savings', 'payment'] },
    { name: 'branchName', label: 'Branch Name', type: 'input', required: false },
    { name: 'fircRequired', label: 'FIRC Required (India)', type: 'select', required: false, options: ['true', 'false'] },
    { name: 'purposeCode', label: 'Purpose Code (India)', type: 'input', required: false },
    { name: 'addressLine1', label: 'Address Line 1', type: 'input', required: false },
    { name: 'addressLine2', label: 'Address Line 2', type: 'input', required: false },
    { name: 'city', label: 'City', type: 'input', required: false },
    { name: 'state', label: 'State', type: 'input', required: false },
    { name: 'postalCode', label: 'Postal Code', type: 'input', required: false },
    { name: 'addressCountry', label: 'Address Country (ISO alpha-2)', type: 'input', required: false },
    { name: 'phoneCallingCode', label: 'Phone Calling Code', type: 'input', required: false, default: '' },
    { name: 'phoneNumber', label: 'Phone Number', type: 'input', required: false },
    { name: 'localPaymentType', label: 'Local Payment Type', type: 'select', required: false, options: ['pix_brl', 'upi_inr', 'promptpay_thb'],
      requiredWhen: { field: 'bindType', value: 'LOCAL_PAYMENT' } },
    { name: 'depositKey', label: 'Deposit Key (PIX/UPI)', type: 'input', required: false,
      requiredWhen: { field: 'bindType', value: 'LOCAL_PAYMENT' } },
    { name: 'depositKeyType', label: 'Deposit Key Type', type: 'input', required: false },
    { name: 'branchCode', label: 'Branch Code', type: 'input', required: false },
    { name: 'bsbCode', label: 'BSB Code (Australia)', type: 'input', required: false },
    { name: 'transitCode', label: 'Transit Code (Canada)', type: 'input', required: false },
    { name: 'institutionNumber', label: 'Institution Number (Canada)', type: 'input', required: false },
    { name: 'clabe', label: 'CLABE (Mexico)', type: 'input', required: false },
    { name: 'nameLocal', label: 'Name Local', type: 'input', required: false },
  ],
  MOOLAHGO: [
    { name: 'callbackUrl', label: '回调地址', type: 'input', required: false, default: 'https://example.com/callback/bindcard' },
    { name: 'bankCity', label: '银行所在城市', type: 'input', required: true, default: '', tip: '必填; 映射渠道 bene_bank_city' },
    { name: 'bankBranch', label: '银行分行名称', type: 'input', required: false, tip: '非必填; 澳洲可选' },
    { name: 'swiftCode', label: 'SWIFT代码', type: 'input', required: false, tip: '条件必填; SWIFT渠道(SHA/OUR/BEN)出金必填; 11位XXX结尾; 与bic至少填一个' },
    { name: 'beneAddress', label: '收款人地址', type: 'input', required: false, tip: '条件必填; HKD必填' },
    { name: 'benePhoneNumber', label: '收款人手机号', type: 'input', required: false, tip: '条件必填; KRW必填' },
    { name: 'beneIcNumber', label: '收款人证件号', type: 'input', required: false, tip: '条件必填; CNY必填; 18位' },
    { name: 'beneRelationship', label: '收款人与汇款人关系', type: 'input', required: false, tip: '条件必填; CNY必填' },
    { name: 'nickname', label: '收款人昵称', type: 'input', required: false, tip: '≤25字符' },
    { name: 'beneType', label: '收款人类型', type: 'select', required: false, options: ['individual', 'company'], tip: '条件必填; CNY必填' },
    { name: 'beneGender', label: '收款人性别', type: 'select', required: false, options: ['M', 'F'], tip: '条件必填; CNY必填/公司主体可选' },
    { name: 'beneNationality', label: '收款人国籍', type: 'input', required: false, tip: '条件必填; CNY必填/公司主体可选; ISO3166' },
    { name: 'beneOccupation', label: '收款人职业', type: 'input', required: false, tip: '条件必填; CNY必填/公司主体可选' },
    { name: 'beneCompanyName', label: '收款人公司名', type: 'input', required: false, tip: '条件必填; CNY且收款个人付款公司时必填' },
    { name: 'beneBsbCode', label: 'BSB Code', type: 'input', required: false, tip: '非必填; 澳洲6字符' },
    { name: 'beneInterSwiftCode', label: '中间行SWIFT', type: 'input', required: false, tip: '非必填; 11位' },
    { name: 'beneEmail', label: '收款人邮箱', type: 'input', required: false, tip: '非必填; ≤50字符' },
  ]
};

export default function BankCardBind() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [bindType, setBindType] = useState('BANKACCOUNT');
  const [userChannels, setUserChannels] = useState([]);
  const [protocol, setProtocol] = useState('clabe');
  const [userAccounts, setUserAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [payoutRequirements, setPayoutRequirements] = useState(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [tazapayPayoutType, setTazapayPayoutType] = useState(null);

  // MDAQ: 独立的走廊状态（不影响 TAZAPAY）
  const [mdaqBankCountry, setMdaqBankCountry] = useState(undefined);
  const [mdaqCurrency, setMdaqCurrency] = useState(undefined);
  const [mdaqRequirements, setMdaqRequirements] = useState(null);
  const [mdaqRequirementsLoading, setMdaqRequirementsLoading] = useState(false);

  // Country → primary currencies mapping (for TAZAPAY)
  const COUNTRY_CURRENCIES = {
    US: ['USD'], GB: ['GBP','EUR'], HK: ['HKD','USD'], SG: ['SGD','USD'],
    IN: ['INR'], JP: ['JPY'], AU: ['AUD'], CA: ['CAD'],
    DE: ['EUR'], FR: ['EUR'], CH: ['CHF','EUR'], NZ: ['NZD'],
    BR: ['BRL'], MX: ['MXN'], AE: ['AED'], SA: ['SAR'],
    ID: ['IDR'], TH: ['THB'], PH: ['PHP'], MY: ['MYR'],
    VN: ['VND'], KR: ['KRW'], CN: ['CNY'], NG: ['NGN'],
    KE: ['KES'], ZA: ['ZAR'], SE: ['SEK'], NO: ['NOK'],
    DK: ['DKK'], PL: ['PLN'], IL: ['ILS'], TR: ['TRY'],
  };
  const { t } = useLanguage();
  const { cache, save: saveCache } = usePageCache('bankCardBind');

  React.useEffect(() => {
    loadUserChannels();
    loadUserAccounts();
    if (cache) {
      if (cache.result) setResult(cache.result);
      if (cache.selectedChannel) setSelectedChannel(cache.selectedChannel);
      if (cache.bindType) setBindType(cache.bindType);
      if (cache.protocol) setProtocol(cache.protocol);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    } else {
      const savedData = localStorage.getItem('bankCardBindFormData');
      if (savedData) {
        try {
          const formData = JSON.parse(savedData);
          form.setFieldsValue(formData);
          if (formData.channelCode) setSelectedChannel(formData.channelCode);
          if (formData.bindType) setBindType(formData.bindType);
        } catch (e) {}
      }
    }
  }, [form]);

  React.useEffect(() => {
    return () => {
      saveCache({ result, selectedChannel, bindType, protocol, formValues: form.getFieldsValue() });
    };
  });

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const responseData = data.data;
      const channelCurrencyMap = responseData?.responseBody || responseData || {};
      const channels = Object.keys(channelCurrencyMap).map(channelCode => ({
        channelCode,
        currencies: [...new Set(channelCurrencyMap[channelCode] || [])]
      }));
      setUserChannels(channels);
      if (channels.length > 0) {
        const first = channels[0];
        form.setFieldsValue({ channelCode: first.channelCode, currency: first.currencies[0] });
        setSelectedChannel(first.channelCode);
      }
    } catch (error) {
      setUserChannels([]);
    }
  };

  const loadUserAccounts = async () => {
    const uid = localStorage.getItem('uid') || localStorage.getItem('username');
    if (!uid) return;
    setAccountsLoading(true);
    try {
      const { data } = await testApi.getUserAccounts(uid);
      const res = data.data?.responseBody;
      let parsed = res;
      if (typeof res === 'string') {
        try { parsed = JSON.parse(res); } catch (e) { parsed = null; }
      }
      if (parsed?.data && Array.isArray(parsed.data)) {
        setUserAccounts(parsed.data);
      } else if (Array.isArray(parsed)) {
        setUserAccounts(parsed);
      } else {
        setUserAccounts([]);
      }
    } catch (error) {
      console.error('Failed to load user accounts:', error);
      setUserAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const saveFormData = (changedValues) => {
    if (changedValues?.extParam_protocol !== undefined) {
      setProtocol(changedValues.extParam_protocol);
    }
    // TAZAPAY: track payoutType changes
    if (changedValues?.extParam_transferType !== undefined) {
      setTazapayPayoutType(changedValues.extParam_transferType);
    }
    // TAZAPAY: 当 bankCountry 或 currency 变化时，自动加载走廊字段要求
    if (selectedChannel === 'TAZAPAY' && (changedValues?.bankCountry || changedValues?.currency)) {
      const values = form.getFieldsValue();
      const country = changedValues?.bankCountry || values.bankCountry;
      const currency = changedValues?.currency || values.currency;
      if (country?.length === 2 && currency?.length === 3) {
        loadPayoutRequirements(country, currency);
      }
    }
    localStorage.setItem('bankCardBindFormData', JSON.stringify(form.getFieldsValue()));
  };

  // TAZAPAY: 加载走廊字段要求
  const loadPayoutRequirements = async (country, currency) => {
    setRequirementsLoading(true);
    try {
      const { data } = await metaApi.getPayoutRequirements('TAZAPAY', country, currency);
      // fiat-payment 返回格式: { code: 200, data: { country, currency, payoutMethods: [...] } }
      const reqData = data?.data || null;
      if (reqData?.payoutMethods?.length > 0) {
        setPayoutRequirements(reqData);
        const firstType = reqData.payoutMethods[0].payoutType;
        setTazapayPayoutType(firstType);
        form.setFieldValue('extParam_transferType', firstType);
      } else {
        setPayoutRequirements(null);
        console.warn('Payout requirements returned empty for', country, currency, data);
      }
    } catch (e) {
      console.error('Failed to load payout requirements:', e);
      setPayoutRequirements(null);
    } finally {
      setRequirementsLoading(false);
    }
  };

  // MDAQ: 加载走廊字段要求（独立于 TAZAPAY）
  const loadMdaqRequirements = async (country, currency) => {
    setMdaqRequirementsLoading(true);
    try {
      const { data } = await metaApi.getPayoutRequirements('MDAQ', country, currency);
      const reqData = data?.data || null;
      if (reqData?.data?.length > 0) {
        setMdaqRequirements(reqData);
      } else {
        setMdaqRequirements(null);
      }
    } catch (e) {
      console.error('Failed to load MDAQ payout requirements:', e);
      setMdaqRequirements(null);
    } finally {
      setMdaqRequirementsLoading(false);
    }
  };

  // TAZAPAY: 获取当前选中的 payoutMethod
  const getSelectedPayoutMethod = () => {
    if (!payoutRequirements?.payoutMethods?.length) return null;
    return payoutRequirements.payoutMethods.find(m => m.payoutType === tazapayPayoutType) || payoutRequirements.payoutMethods[0];
  };

  // TAZAPAY: 判断字段是否被 requirements 标记为必填
  const isTazapayFieldRequired = (fieldName) => {
    const pm = getSelectedPayoutMethod();
    if (!pm) return false;
    const req = pm.requiredFields;
    return (req?.mainFields || []).includes(fieldName) || (req?.extParamFields || []).includes(fieldName);
  };

  // TAZAPAY: 判断字段是否被 requirements 提到（必填或推荐）
  const isTazapayFieldRelevant = (fieldName) => {
    const pm = getSelectedPayoutMethod();
    if (!pm) return true;
    const req = pm.requiredFields;
    const rec = pm.recommendedFields;
    return (req?.mainFields || []).includes(fieldName) ||
           (req?.extParamFields || []).includes(fieldName) ||
           (rec?.mainFields || []).includes(fieldName) ||
           (rec?.extParamFields || []).includes(fieldName);
  };

  const handleChannelChange = (channelCode) => {
    setSelectedChannel(channelCode);
    setPayoutRequirements(null); // 切换渠道时清除走廊要求
    // MDAQ: 清除独立状态
    setMdaqBankCountry(undefined);
    setMdaqCurrency(undefined);
    setMdaqRequirements(null);
    const channel = userChannels.find(c => c.channelCode === channelCode);
    if (channelCode === 'MDAQ') {
      form.setFieldsValue({ currency: undefined });
    } else if (channel?.currencies.length > 0) {
      form.setFieldsValue({ currency: channel.currencies[0] });
    }
    // 切换渠道时清空 accountId，因为过滤列表变了
    form.setFieldsValue({ accountId: undefined, userType: (channelCode === 'TAZAPAY' || channelCode === 'MDAQ' || channelCode === 'MOOLAHGO') ? 'BUSINESS' : 'INDIVIDUAL' });
    // Durianpay/MOOLAHGO 只支持 BANKACCOUNT，切换时重置
    if (channelCode === 'DURIANPAY' || channelCode === 'MOOLAHGO') {
      form.setFieldsValue({ bindType: 'BANKACCOUNT' });
      setBindType('BANKACCOUNT');
    }
    const resetValues = {};
    (channelExtParamFields[channelCode] || []).forEach(field => {
      if (field.default) resetValues[`extParam_${field.name}`] = field.default;
    });
    form.setFieldsValue(resetValues);
    // 重置 protocol state
    const protocolDefault = (channelExtParamFields[channelCode] || []).find(f => f.name === 'protocol')?.default || 'clabe';
    setProtocol(protocolDefault);
    saveFormData();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const extParam = {};
      Object.keys(values).forEach(key => {
        if (key.startsWith('extParam_')) {
          extParam[key.replace('extParam_', '')] = values[key];
        }
      });

      const requestBody = {
        accountId: values.accountId,
        channelCode: values.channelCode,
        externalUserId: localStorage.getItem('uid') || localStorage.getItem('username'),
        accountName: values.accountName,
        userType: values.userType,
        bic: values.bic,
        bankCountry: values.bankCountry,
        currency: values.currency,
        bankName: values.bankName,
        bindType: values.bindType
      };

      // 只在有 extParam 值时才添加（后端期望 JSON 字符串）
      if (Object.keys(extParam).length > 0) {
        requestBody.extParam = JSON.stringify(extParam);
      }

      // iban 和 accountNumber 按实际填写情况传，后端按有无值判断
      if (values.iban) requestBody.iban = values.iban;
      if (values.accountNumber) requestBody.accountNumber = values.accountNumber;

      const response = await testApi.executeTest({
        testType: 'BANK_BIND',
        channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'DEMO_ACCOUNT',
        currency: values.currency,
        env: getEnvByHostname(),
        executor: localStorage.getItem('username'),
        requestBody: JSON.stringify(requestBody)
      });

      const result = response.data;
      if (result.data?.responseBody && typeof result.data.responseBody === 'string') {
        try { result.data.responseBody = JSON.parse(result.data.responseBody); } catch (e) {}
      }
      setResult(result);

      const rbMsg = result.data?.responseBody?.msg;
      if (result.code === '200') message.success(rbMsg || t('bindSuccess'));
      else message.warning(`${t('bindFailed')}: ${rbMsg || result.msg || t('failed')}`);
    } catch (error) {
      message.error(t('bindFailed') + ': ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setSelectedChannel(null);
  };

  const extFields = selectedChannel ? (channelExtParamFields[selectedChannel] || []) : [];

  // TAZAPAY: 根据 requirements 动态构建扩展字段
  const resolvedExtFields = (() => {
    if (selectedChannel !== 'TAZAPAY' || !payoutRequirements?.payoutMethods?.length) return extFields;
    const pm = getSelectedPayoutMethod();
    if (!pm) return extFields;
    const reqExt = pm.requiredFields?.extParamFields || [];
    const recExt = pm.recommendedFields?.extParamFields || [];
    // transferType 由下方专属的 Transfer Type 下拉框渲染，这里排除避免重复注册同名 Form.Item
    const allExt = [...new Set([...reqExt, ...recExt])].filter(name => name !== 'transferType');
    const fieldMap = Object.fromEntries(extFields.map(f => [f.name, f]));
    return allExt.map(name => {
      const base = fieldMap[name] || { name, label: name, type: 'input' };
      return { ...base, required: reqExt.includes(name) };
    });
  })();

  // MDAQ: 根据走廊返回动态构建扩展字段（独立于 TAZAPAY）
  const mdaqExtFields = (() => {
    if (selectedChannel !== 'MDAQ' || !mdaqRequirements?.data?.length) return [];
    // 取 company entityType（KYB 场景）
    const entity = mdaqRequirements.data.find(e => e.entityType === 'company') || mdaqRequirements.data[0];
    const fieldMap = Object.fromEntries(extFields.map(f => [f.name, f]));
    // bankAccount 下已映射到主参数的字段，不放进 extParam
    const bankAccountMainParamFields = new Set(['country', 'currency', 'accountNumber', 'bicSwift', 'bankName', 'bankAccountHolderName']);
    const dynamicFields = [];
    Object.entries(entity).forEach(([key, value]) => {
      if (key === 'entityType') return;
      if (key === 'bankAccount' && typeof value === 'object') {
        // bankAccount 子字段：排除已映射到主参数的，剩余的以原名放入 extParam
        Object.entries(value).forEach(([bKey, bVal]) => {
          if (bankAccountMainParamFields.has(bKey)) return; // 已由主参数覆盖
          const base = fieldMap[bKey] || { name: bKey, label: bKey, type: 'input' };
          dynamicFields.push({ ...base, name: bKey, required: true, tip: `(bankAccount) 正则: ${bVal}` });
        });
      } else {
        const base = fieldMap[key] || { name: key, label: key, type: 'input' };
        dynamicFields.push({ ...base, required: true, tip: `正则: ${value}` });
      }
    });
    return dynamicFields;
  })();

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px', borderRadius: '8px', marginBottom: '16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <BankOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('bankCardBind')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} type="text" style={{ color: '#fff' }}>{t('reset')}</Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={saveFormData}>
                <Form.Item name="businessPartner" hidden><Input /></Form.Item>

                {/* ── 渠道配置 ── */}
                {sectionTitle('⚙️', t('channelConfig'))}
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item label={t('channel')} name="channelCode" rules={[{ required: true }]}>
                      <Select
                        placeholder={t('selectChannel')}
                        onChange={handleChannelChange}
                        options={buildChannelOptions(userChannels.map(c => c.channelCode))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t('currency')} name="currency" rules={[{ required: true }]} initialValue="USD">
                      {selectedChannel === 'TAZAPAY' ? (
                        <Select
                          showSearch
                          allowClear
                          placeholder={form.getFieldValue('bankCountry') ? 'Select Currency' : 'Select Country first'}
                          disabled={!form.getFieldValue('bankCountry')}
                          onChange={v => {
                            const country = form.getFieldValue('bankCountry');
                            if (country && v) loadPayoutRequirements(country, v);
                            else setPayoutRequirements(null);
                          }}
                          options={(() => {
                            const country = form.getFieldValue('bankCountry');
                            const currencies = country ? (COUNTRY_CURRENCIES[country] || []) : [];
                            return currencies.map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }));
                          })()}
                          filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                        />
                      ) : selectedChannel === 'MDAQ' ? (
                        <Select
                          showSearch
                          allowClear
                          placeholder={mdaqBankCountry ? 'Select Currency' : 'Select Country first'}
                          disabled={!mdaqBankCountry}
                          value={mdaqCurrency}
                          onChange={v => {
                            setMdaqCurrency(v);
                            form.setFieldValue('currency', v);
                            if (mdaqBankCountry && v) loadMdaqRequirements(mdaqBankCountry, v);
                            else setMdaqRequirements(null);
                          }}
                          options={(mdaqBankCountry ? (COUNTRY_CURRENCIES[mdaqBankCountry] || []) : []).map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }))}
                          filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                        />
                      ) : (
                        <Select options={(userChannels.find(c => c.channelCode === form.getFieldValue('channelCode'))?.currencies || []).map(c => ({ label: c, value: c }))} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                {/* TAZAPAY 后端 accountId 非必填（有值时才校验），前端不强校验 */}
                <Form.Item label={t('accountId')} name="accountId" rules={[{ required: !['BANK_FRICK', 'PYVIO', 'DA5', 'TAZAPAY'].includes(selectedChannel) }]}>
                  <Select
                    placeholder={t('pleaseSelect', t('accountId'))}
                    allowClear
                    loading={accountsLoading}
                    showSearch
                    optionFilterProp="label"
                    notFoundContent={accountsLoading ? t('loading') : t('noAccountData')}
                    options={userAccounts
                      .filter(acc => acc.status === 'ACTIVE' && (!selectedChannel || acc.channelCode === selectedChannel))
                      .map(acc => ({
                        label: `${acc.accountId} (${acc.channelCode} · ${acc.currency} · ${acc.status})`,
                        value: acc.accountId
                      }))}
                  />
                </Form.Item>

                <Divider style={{ margin: '20px 0 16px' }} />

                {/* ── 账户持有人信息 ── */}
                {sectionTitle('👤', t('accountHolder'))}
                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item label={t('accountName')} name="accountName" rules={[{ required: true }]}>
                      <Input placeholder="e.g. John Michael Doe" prefix={<UserOutlined style={{ color: '#d1d5db' }} />} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t('holderType')} name="userType" rules={[{ required: true }]} initialValue={(selectedChannel === 'TAZAPAY' || selectedChannel === 'MDAQ' || selectedChannel === 'MOOLAHGO') ? 'BUSINESS' : 'INDIVIDUAL'}>
                      <Select options={(selectedChannel === 'TAZAPAY' || selectedChannel === 'MDAQ' || selectedChannel === 'MOOLAHGO')
                        ? [{ label: 'BUSINESS', value: 'BUSINESS' }]
                        : [{ label: 'INDIVIDUAL', value: 'INDIVIDUAL' }, { label: 'BUSINESS', value: 'BUSINESS' }]
                      } />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '20px 0 16px' }} />

                {/* ── 银行账户信息 ── */}
                {sectionTitle('🏦', t('bankAccountInfo'))}
                <Row gutter={16}>
                  {selectedChannel !== 'TAZAPAY' && selectedChannel !== 'MDAQ' && (
                  <Col span={12}>
                    <Form.Item
                      label={t('accountType')}
                      name="bindType"
                      rules={[{ required: ['BANK_FRICK', 'BANKING_CIRCLE'].includes(selectedChannel) }]}
                      initialValue="BANKACCOUNT"
                    >
                      <Select
                        onChange={(value) => setBindType(value)}
                        options={selectedChannel === 'DURIANPAY' || selectedChannel === 'MOOLAHGO'
                          ? [{ label: 'Account Number', value: 'BANKACCOUNT' }]
                          : [
                              { label: 'Account Number', value: 'BANKACCOUNT' },
                              { label: 'IBAN', value: 'IBAN' }
                            ]}
                      />
                    </Form.Item>
                  </Col>
                  )}
                  <Col span={12}>
                    <Form.Item
                      label={selectedChannel === 'DURIANPAY' ? 'BIC' : 'BIC / SWIFT Code'}
                      name="bic"
                      rules={[{ required: ['SGB', 'EQUALS_MONEY', 'KORAPAY', 'BANK_FRICK', 'DURIANPAY', 'DA5'].includes(selectedChannel) || (selectedChannel === 'TAZAPAY' && isTazapayFieldRequired('bic')) }]}
                    >
                      <Input placeholder={selectedChannel === 'DURIANPAY' ? 'e.g. BRI(002)、BCA(014)、MANDIRI(008)、PERMATA(013)、CIMB(022)、BNI(009)、NOBU(503)' : 'e.g. DEUTDEDB'} />
                    </Form.Item>
                  </Col>
                </Row>

                {/* LEAN_TECH / TAZAPAY 同时需要 iban + accountNumber；其他渠道按 bindType 切换 */}
                <Row gutter={16}>
                  <Col span={12}>
                    {(bindType === 'IBAN' || ['LEAN_TECH', 'TAZAPAY'].includes(selectedChannel)) ? (
                      <Form.Item label="IBAN" name="iban" rules={[
                        { required: selectedChannel !== 'TAZAPAY' },
                        ...(selectedChannel === 'TAZAPAY' ? [{ validator: (_, v) => v || form.getFieldValue('accountNumber') ? Promise.resolve() : Promise.reject(new Error('IBAN or Account Number is required')) }] : [])
                      ]}>
                        <Input placeholder="e.g. GB29 NWBK 6016 1331 9268 19" onChange={() => { if (selectedChannel === 'TAZAPAY') form.validateFields(['accountNumber']); }} />
                      </Form.Item>
                    ) : (
                      <Form.Item label={t('accountNumber')} name="accountNumber" rules={[{ required: true }]}>
                        <Input placeholder="e.g. 1234567890" />
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={t('bankName')}
                      name="bankName"
                      rules={['BANK_FRICK', 'PYVIO'].includes(selectedChannel) || bindType === 'BANKACCOUNT' ? [{ required: true }] : []}
                    >
                      <Input placeholder="e.g. Deutsche Bank" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* LEAN_TECH / TAZAPAY 额外显示 accountNumber */}
                {['LEAN_TECH', 'TAZAPAY'].includes(selectedChannel) && (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label={t('accountNumber')} name="accountNumber" rules={[
                        { required: selectedChannel !== 'TAZAPAY' },
                        ...(selectedChannel === 'TAZAPAY' ? [{ validator: (_, v) => v || form.getFieldValue('iban') ? Promise.resolve() : Promise.reject(new Error('IBAN or Account Number is required')) }] : [])
                      ]}>
                        <Input placeholder="e.g. 1234567890" onChange={() => form.validateFields(['iban'])} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={t('bankCountry')} name="bankCountry" rules={[{ required: ['SGB', 'EQUALS_MONEY', 'KORAPAY', 'PYVIO', 'BANKING_CIRCLE', 'TAZAPAY', 'MDAQ', 'DA5', 'MOOLAHGO'].includes(selectedChannel) }, { len: 2, message: 'Must be 2-letter country code, e.g. US' }]}>
                      {selectedChannel === 'TAZAPAY' ? (
                        <Select showSearch allowClear placeholder="Select Country"
                          onChange={v => {
                            form.setFieldValue('bankCountry', v);
                            const currencies = v ? (COUNTRY_CURRENCIES[v] || []) : [];
                            if (currencies.length === 1) {
                              form.setFieldValue('currency', currencies[0]);
                              loadPayoutRequirements(v, currencies[0]);
                            } else {
                              form.setFieldValue('currency', undefined);
                              setPayoutRequirements(null);
                            }
                          }}
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={[
                            {value:'US',label:'🇺🇸 US'},{value:'GB',label:'🇬🇧 GB'},{value:'HK',label:'🇭🇰 HK'},{value:'SG',label:'🇸🇬 SG'},
                            {value:'IN',label:'🇮🇳 IN'},{value:'JP',label:'🇯🇵 JP'},{value:'AU',label:'🇦🇺 AU'},{value:'CA',label:'🇨🇦 CA'},
                            {value:'DE',label:'🇩🇪 DE'},{value:'FR',label:'🇫🇷 FR'},{value:'CH',label:'🇨🇭 CH'},{value:'NZ',label:'🇳🇿 NZ'},
                            {value:'BR',label:'🇧🇷 BR'},{value:'MX',label:'🇲🇽 MX'},{value:'AE',label:'🇦🇪 AE'},{value:'SA',label:'🇸🇦 SA'},
                            {value:'ID',label:'🇮🇩 ID'},{value:'TH',label:'🇹🇭 TH'},{value:'PH',label:'🇵🇭 PH'},{value:'MY',label:'🇲🇾 MY'},
                            {value:'VN',label:'🇻🇳 VN'},{value:'KR',label:'🇰🇷 KR'},{value:'CN',label:'🇨🇳 CN'},{value:'NG',label:'🇳🇬 NG'},
                            {value:'KE',label:'🇰🇪 KE'},{value:'ZA',label:'🇿🇦 ZA'},{value:'SE',label:'🇸🇪 SE'},{value:'NO',label:'🇳🇴 NO'},
                            {value:'DK',label:'🇩🇰 DK'},{value:'PL',label:'🇵🇱 PL'},{value:'IL',label:'🇮🇱 IL'},{value:'TR',label:'🇹🇷 TR'},
                          ]}
                        />
                      ) : selectedChannel === 'MDAQ' ? (
                        <Select showSearch allowClear placeholder="Select Country"
                          value={mdaqBankCountry}
                          onChange={v => {
                            setMdaqBankCountry(v);
                            form.setFieldValue('bankCountry', v);
                            setMdaqRequirements(null);
                            const currencies = v ? (COUNTRY_CURRENCIES[v] || []) : [];
                            if (currencies.length === 1) {
                              setMdaqCurrency(currencies[0]);
                              form.setFieldValue('currency', currencies[0]);
                              loadMdaqRequirements(v, currencies[0]);
                            } else {
                              setMdaqCurrency(undefined);
                              form.setFieldValue('currency', undefined);
                            }
                          }}
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          options={[
                            {value:'US',label:'🇺🇸 US'},{value:'GB',label:'🇬🇧 GB'},{value:'HK',label:'🇭🇰 HK'},{value:'SG',label:'🇸🇬 SG'},
                            {value:'IN',label:'🇮🇳 IN'},{value:'JP',label:'🇯🇵 JP'},{value:'AU',label:'🇦🇺 AU'},{value:'CA',label:'🇨🇦 CA'},
                            {value:'DE',label:'🇩🇪 DE'},{value:'FR',label:'🇫🇷 FR'},{value:'CH',label:'🇨🇭 CH'},{value:'NZ',label:'🇳🇿 NZ'},
                            {value:'BR',label:'🇧🇷 BR'},{value:'MX',label:'🇲🇽 MX'},{value:'AE',label:'🇦🇪 AE'},{value:'SA',label:'🇸🇦 SA'},
                            {value:'ID',label:'🇮🇩 ID'},{value:'TH',label:'🇹🇭 TH'},{value:'PH',label:'🇵🇭 PH'},{value:'MY',label:'🇲🇾 MY'},
                            {value:'VN',label:'🇻🇳 VN'},{value:'KR',label:'🇰🇷 KR'},{value:'CN',label:'🇨🇳 CN'},{value:'NG',label:'🇳🇬 NG'},
                            {value:'KE',label:'🇰🇪 KE'},{value:'ZA',label:'🇿🇦 ZA'},{value:'SE',label:'🇸🇪 SE'},{value:'NO',label:'🇳🇴 NO'},
                            {value:'DK',label:'🇩🇰 DK'},{value:'PL',label:'🇵🇱 PL'},{value:'IL',label:'🇮🇱 IL'},{value:'TR',label:'🇹🇷 TR'},
                          ]}
                        />
                      ) : (
                        <Input placeholder="ISO 3166-1 alpha-2, e.g. GB" maxLength={2} style={{ textTransform: 'uppercase' }} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── 渠道扩展参数 ──
                     TAZAPAY 有走廊要求时即使无额外扩展字段也要渲染，
                     否则 Transfer Type 的 Form.Item 不挂载，提交时 extParam_transferType 会丢失 */}
                {(resolvedExtFields.length > 0 || (selectedChannel === 'TAZAPAY' && payoutRequirements)) && (
                  <>
                    <Divider style={{ margin: '20px 0 16px' }} />
                    {sectionTitle('🔧', `${t('extParams')} · ${selectedChannel}`)}
                    {selectedChannel === 'TAZAPAY' && payoutRequirements && (
                      <>
                        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, fontSize: 12, color: '#1d39c4' }}>
                          💡 Fields marked with * are required for this corridor ({form.getFieldValue('bankCountry')}/{form.getFieldValue('currency')})
                        </div>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item label="Transfer Type" name="extParam_transferType" rules={[{ required: true }]}
                              initialValue={payoutRequirements.payoutMethods[0]?.payoutType}>
                              <Select
                                onChange={v => setTazapayPayoutType(v)}
                                options={payoutRequirements.payoutMethods.map(m => ({ label: m.payoutType, value: m.payoutType }))}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    )}
                    {selectedChannel === 'TAZAPAY' && requirementsLoading && (
                      <div style={{ marginBottom: 12, fontSize: 12, color: '#999' }}>Loading corridor requirements...</div>
                    )}
                    <Row gutter={16}>
                      {resolvedExtFields.map(field => {
                        const depField = field.requiredWhen?.field;
                        const getLabel = () => t(field.label) || t(field.name) || field.label;
                        const getTip = (depVal) => {
                          if (!field.tip) return null;
                          const tipFn = t(field.tip);
                          return typeof tipFn === 'function' ? tipFn(depVal) : tipFn;
                        };

                        // 有动态 tip 依赖字段：独立组件接收 protocol prop
                        if (field.tipDep) {
                          return (
                            <Col span={12} key={field.name}>
                              <DynamicTipFormItem field={field} protocol={protocol} t={t} />
                            </Col>
                          );
                        }

                        // 静态 tip
                        if (!depField) {
                          const tip = getTip();
                          return (
                            <Col span={12} key={field.name}>
                              <Form.Item
                                label={getLabel()}
                                tooltip={tip || undefined}
                                name={`extParam_${field.name}`}
                                rules={[{ required: field.required, message: t('pleaseInput', getLabel()) }]}
                                initialValue={field.default}
                              >
                                {field.type === 'select' ? (
                                  <Select
                                    placeholder={t('pleaseSelect', getLabel())}
                                    allowClear
                                    onChange={field.name === 'protocol' ? (val) => setProtocol(val) : undefined}
                                    options={field.options.map(o => ({ label: o, value: o }))}
                                  />
                                ) : (
                                  <Input placeholder={t('pleaseInput', getLabel())} />
                                )}
                              </Form.Item>
                            </Col>
                          );
                        }

                        // 条件必填
                        return (
                          <Col span={12} key={field.name}>
                            <Form.Item
                              noStyle
                              shouldUpdate={(prev, curr) => prev[depField] !== curr[depField]}
                            >
                              {({ getFieldValue }) => {
                                const cur = getFieldValue(depField);
                                const isRequired = field.requiredWhen.values
                                  ? field.requiredWhen.values.includes(cur)
                                  : cur === field.requiredWhen.value;
                                const tip = getTip();
                                return (
                                  <Form.Item
                                    label={getLabel()}
                                    tooltip={tip || undefined}
                                    name={`extParam_${field.name}`}
                                    rules={[{ required: isRequired, message: t('pleaseInput', getLabel()) }]}
                                    initialValue={field.default}
                                  >
                                    <Input placeholder={t('pleaseInput', getLabel())} />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>
                        );
                      })}
                    </Row>
                  </>
                )}

                {/* ── MDAQ 扩展参数（独立逻辑） ── */}
                {selectedChannel === 'MDAQ' && mdaqExtFields.length > 0 && (
                  <>
                    <Divider style={{ margin: '20px 0 16px' }} />
                    {sectionTitle('🔧', `${t('extParams')} · MDAQ`)}
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, fontSize: 12, color: '#1d39c4' }}>
                      💡 Fields marked with * are required for this corridor ({mdaqBankCountry}/{mdaqCurrency})
                    </div>
                    {mdaqRequirementsLoading && (
                      <div style={{ marginBottom: 12, fontSize: 12, color: '#999' }}>Loading corridor requirements...</div>
                    )}
                    <Row gutter={16}>
                      {mdaqExtFields.map(field => (
                        <Col span={12} key={field.name}>
                          <Form.Item
                            label={<Space size={4}><span>{field.label}</span>{field.tip && <span style={{ cursor: 'help', color: '#1890ff', fontSize: 12 }} title={field.tip}>?</span>}</Space>}
                            name={`extParam_${field.name}`}
                            rules={[{ required: field.required, message: `${field.label} is required` }]}
                          >
                            <Input placeholder={field.tip || field.label} />
                          </Form.Item>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<LinkOutlined />}
                  loading={loading}
                  size="large"
                  block
                  className="primary-button"
                  style={{ height: 48, marginTop: 8 }}
                >
                  {loading ? t('binding') : t('bindBankAccount')}
                </Button>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card className="modern-card" title={`📋 ${t('bindResult')}`} bodyStyle={{ padding: '24px' }}>
              {result ? (
                <div className="json-viewer">
                  <ReactJson
                    src={result}
                    collapsed={false}
                    theme="rjv-default"
                    displayDataTypes={false}
                    displayObjectSize={false}
                    enableClipboard={true}
                  />
                </div>
              ) : (
                <div style={{
                  padding: 60, textAlign: 'center', color: '#999',
                  background: '#f9fafb', borderRadius: 8, border: '2px dashed #d1d5db'
                }}>
                  <BankOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>{t('submitToShowBindResult')}</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
